import { z } from "zod";
import { Symbol_ } from "./primitives";

/**
 * What the page the user is looking at can contribute to a question.
 *
 * "Why is this ranked here?" only makes sense if the assistant knows which
 * ticker page it was asked from. The route is carried for logging and future
 * use; the symbols are what retrieval actually consumes, as seeds. Nothing
 * here is trusted beyond that — an unknown ticker is silently dropped rather
 * than grounded on.
 */
export const PageContext = z.object({
  route: z.string().max(200),
  symbol: Symbol_.optional(),
  symbols: z.array(Symbol_).max(20).optional(),
});
export type PageContext = z.infer<typeof PageContext>;

/** What the browser sends. Deliberately carries no identity and no history. */
export const ChatRequest = z.object({
  question: z.string().min(1).max(1000),
  pageContext: PageContext.optional(),
});
export type ChatRequest = z.infer<typeof ChatRequest>;

/**
 * One stored message.
 *
 * `groundedIn` is kept on assistant messages so a follow-up turn can seed
 * retrieval with the symbols the previous answer was actually about —
 * "what about its risk flags?" has to resolve "its" somehow, and the last
 * answer's grounding is the honest interpretation.
 */
export const ChatMessage = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(20_000),
  at: z.string(),
  groundedIn: z
    .object({ symbols: z.array(z.string()), themes: z.array(z.string()) })
    .optional(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/**
 * Bounded so one document cannot grow without limit.
 *
 * Questions are capped at 1,000 characters and answers at 1,200 completion
 * tokens, so 60 messages stays around 350 KB worst case — far under the 2 MB
 * Cosmos document limit, and small enough that the per-turn upsert stays
 * cheap.
 */
export const MAX_STORED_MESSAGES = 60;

export const CHAT_SCHEMA_VERSION = 1;

/**
 * A stored conversation — one per user.
 *
 * `id` equals `userId` for the same reason as PortfolioDocument: the container
 * is partitioned on `/userId`, so every access is a point read or write at
 * ~1 RU, and the key derives only from the platform-injected principal header,
 * never from the request body.
 */
export const ConversationDocument = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  schemaVersion: z.literal(CHAT_SCHEMA_VERSION),
  messages: z.array(ChatMessage).max(MAX_STORED_MESSAGES),
  updatedAt: z.string(),
});
export type ConversationDocument = z.infer<typeof ConversationDocument>;

/** What the browser receives from GET /api/chat. */
export const ChatHistoryResponse = z.object({
  messages: z.array(ChatMessage),
  updatedAt: z.string().nullable(),
  /** False when no conversation exists yet, which is not an error. */
  exists: z.boolean(),
  /** True when storage was unreachable and history could not be loaded. */
  degraded: z.boolean().optional(),
});
export type ChatHistoryResponse = z.infer<typeof ChatHistoryResponse>;

export function buildConversation(userId: string, messages: ChatMessage[]): ConversationDocument {
  return ConversationDocument.parse({
    id: userId,
    userId,
    schemaVersion: CHAT_SCHEMA_VERSION,
    // Oldest messages fall off first; the recent turns are the conversation.
    messages: messages.slice(-MAX_STORED_MESSAGES),
    updatedAt: new Date().toISOString(),
  });
}
